import React from "react";
import { createPortal } from "react-dom";
import type { StylesConfig } from "react-select";
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  FileUploader,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
  Modal,
  Radio,
  Tabs,
  TextField,
} from "@hid-galaxy-ui/galaxy-react";
import { Dialog, DialogContent } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import {
  addOnboardingDetailSagaAction,
  getOnboardingDetailListSagaAction,
  updateOnboardingDetailSagaAction,
  getFileViewUrlSagaAction,
} from "../../../../../../../redux/saga/onboarding-form/saga-actions";
import { getProjectsListSagaAction } from "../../../../../../../redux/saga/projects/saga-actions";
import { getTechnologiesSagaAction } from "../../../../../../../redux/saga/technology-settings/technologies/saga-actions";
import { onboardingFormSliceAction } from "../../../../../../../redux/slices/onboarding-form";
import { OnboardingFormData, EnvironmentKey } from "./types";
import { OnboardingFormDetails } from "../../../../../../../redux/slices/onboarding-form/Types";
import { getValidDateOrNull } from "../../../../../../../utils/getValidDateOrNull";
import "./OnboardingModal.scss";

/** Normalize edit/API integration_ids to selected setting IDs for checkbox state. */
const toSelectedIntegrationIds = (
  integrationIds: OnboardingFormDetails["integration_ids"] | undefined
): number[] => {
  if (!integrationIds?.length) return [];
  return integrationIds
    .map((item) =>
      typeof item === "number" ? Number(item) : Number(item.integration_id)
    )
    .filter((id) => Number.isFinite(id) && id > 0);
};

interface OnboardingModalProps {
  mode: "ADD" | "UPDATE";
  onClose: () => void;
  partnerId: number;
  onboardingData?: OnboardingFormDetails;
}

type DropdownOption = { label: string; value: string | number };

const ONBOARDING_MODAL_Z_INDEX = 10000;

const modalDropdownMenuStyles: StylesConfig<DropdownOption, false> = {
  menuPortal: (base) => ({
    ...base,
    zIndex: ONBOARDING_MODAL_Z_INDEX,
  }),
  menu: (base) => ({
    ...base,
    zIndex: ONBOARDING_MODAL_Z_INDEX + 1,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#e6e6e6" : base.backgroundColor,
    color: base.color,
    ":active": {
      backgroundColor: "#e6e6e6",
    },
  }),
};

const dropdownMenuPortalTarget =
  typeof document !== "undefined" ? document.body : undefined;

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required = false,
}) => (
  <label className="field-label">
    {label}
    {required ? <span aria-hidden="true">*</span> : null}
  </label>
);

const FormSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="onboarding-form-section">
    <h3 className="onboarding-form-section__title">{title}</h3>
    <div className="onboarding-form-section__card">{children}</div>
  </section>
);

const ModalHeaderSubtitle: React.FC<{ text: string }> = ({ text }) => {
  const [headlineEl, setHeadlineEl] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    setHeadlineEl(
      document.querySelector(
        ".onboarding-modal .hid-modal__header-headline"
      ) as HTMLElement | null
    );
  }, [text]);

  if (!headlineEl) {
    return null;
  }

  return createPortal(
    <p className="onboarding-modal__subtitle">{text}</p>,
    headlineEl
  );
};

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  mode,
  onClose,
  partnerId,
  onboardingData,
}) => {
  const [formSubmitted, setFormSubmitted] = React.useState<boolean>(false);
  const [onboardingDetails, setOnboardingDetails] =
    React.useState<OnboardingFormData>({
      status: "In Progress",
      technology_id: null,
      integration_ids: [],
      projectName: "",
      cert: {
        test_org_name: "",
        test_org_id: "",
        org_admin: "",
      },
      preprod: {
        test_org_name: "",
        test_org_id: "",
        org_admin: "",
      },
      prod: {
        test_org_name: "",
        test_org_id: "",
        org_admin: "",
      },
      startDate: null,
      consultant_id: null,
      nocfReaderSentDate: null,
      nocfReaderDocument: null,
      nocfLicensesSentDate: null,
      nocfLicensesDocument: null,
      portalAccessGrantedDate: null,
    });

  const [selectedProjectOption, setSelectedProjectOption] = React.useState<{
    label: string;
    value: string;
  } | null>(null);
  const [nocfReaderFile, setNocfReaderFile] = React.useState<File | null>(null);
  const [nocfLicensesFile, setNocfLicensesFile] = React.useState<File | null>(
    null
  );
  const [showIntegrationConfirm, setShowIntegrationConfirm] =
    React.useState(false);
  const [awaitingIntegrationConfirmResponse, setAwaitingIntegrationConfirmResponse] =
    React.useState(false);
  const pendingUpdatePayloadRef =
    React.useRef<OnboardingFormDetails | null>(null);
  const sawUpdateLoadingRef = React.useRef(false);

  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.onboarding_form);
  const { technologies, status: technologySettingsStatus } = useAppSelector(
    (state) => state.technology_settings_technologies
  );
  const { userListHID } = useAppSelector((state) => state.user);
  const { onboardingDetailList } = useAppSelector(
    (state) => state.onboarding_form
  );
  const { loggedUserDetail } = useAppSelector((state) => state.auth);

  React.useEffect(() => {
    document.body.classList.add("onboarding-modal-open");
    return () => {
      document.body.classList.remove("onboarding-modal-open");
      dispatch(onboardingFormSliceAction.clearUpdateOnboardingConfirmation());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!awaitingIntegrationConfirmResponse) {
      sawUpdateLoadingRef.current = false;
      return;
    }

    if (status.updateOnboardingDetailLoading) {
      sawUpdateLoadingRef.current = true;
      return;
    }

    // Wait until loading has flipped true → false so we don't finish before the request starts
    if (!sawUpdateLoadingRef.current) {
      return;
    }

    sawUpdateLoadingRef.current = false;

    if (status.updateOnboardingConfirmationRequired) {
      setShowIntegrationConfirm(true);
      setAwaitingIntegrationConfirmResponse(false);
      return;
    }

    setFormSubmitted(true);
    setAwaitingIntegrationConfirmResponse(false);
  }, [
    awaitingIntegrationConfirmResponse,
    status.updateOnboardingDetailLoading,
    status.updateOnboardingConfirmationRequired,
  ]);

  React.useEffect(() => {
    if (technologies.length === 0) {
      dispatch(getTechnologiesSagaAction());
    }
  }, [dispatch, technologies.length]);

  const activeTechnologies = React.useMemo(
    () => technologies.filter((technology) => technology.status === "Active"),
    [technologies]
  );

  const selectedTechnology = React.useMemo(
    () =>
      activeTechnologies.find(
        (technology) =>
          Number(technology.id) === Number(onboardingDetails.technology_id)
      ) || null,
    [activeTechnologies, onboardingDetails.technology_id]
  );

  const activeIntegrations = React.useMemo(
    () =>
      selectedTechnology?.integrations.filter(
        (integration) => integration.status === "Active"
      ) || [],
    [selectedTechnology]
  );

  React.useEffect(() => {
    if (mode === "UPDATE" && onboardingData) {
      setOnboardingDetails({
        status: onboardingData.status || "In Progress",
        technology_id: onboardingData.technology_id ?? null,
        integration_ids: toSelectedIntegrationIds(
          onboardingData.integration_ids
        ),
        projectName: onboardingData.project_name || "",
        cert: {
          test_org_name: onboardingData.cert?.test_org_name || "",
          test_org_id: onboardingData.cert?.test_org_id || "",
          org_admin: onboardingData.cert?.org_admin || "",
        },
        preprod: {
          test_org_name: onboardingData.preprod?.test_org_name || "",
          test_org_id: onboardingData.preprod?.test_org_id || "",
          org_admin: onboardingData.preprod?.org_admin || "",
        },
        prod: {
          test_org_name: onboardingData.prod?.test_org_name || "",
          test_org_id: onboardingData.prod?.test_org_id || "",
          org_admin: onboardingData.prod?.org_admin || "",
        },
        startDate: getValidDateOrNull(onboardingData.start_date),
        consultant_id: onboardingData.consultant_id || null,
        nocfReaderSentDate: getValidDateOrNull(
          onboardingData.nocf_reader_sent_date
        ),
        nocfReaderDocument: onboardingData.nocf_reader_document || null,
        nocfLicensesSentDate: getValidDateOrNull(
          onboardingData.nocf_licenses_sent_date
        ),
        nocfLicensesDocument: onboardingData.nocf_licenses_document || null,
        portalAccessGrantedDate: getValidDateOrNull(
          onboardingData.portal_access_granted_date
        ),
      });

      // Set project name dropdown
      const projectName = onboardingData.project_name;
      const existingProject = onboardingDetailList.find(
        (project) => project.project_name === projectName
      );
      if (existingProject) {
        setSelectedProjectOption({
          label: projectName,
          value: projectName,
        });
      } else {
        setSelectedProjectOption({
          label: "Enter New Project Name",
          value: "Enter New Project Name",
        });
      }
    } else if (mode === "ADD") {
      // Reset form for ADD mode
      setOnboardingDetails({
        status: "In Progress",
        technology_id: null,
        integration_ids: [],
        projectName: "",
        cert: {
          test_org_name: "",
          test_org_id: "",
          org_admin: "",
        },
        preprod: {
          test_org_name: "",
          test_org_id: "",
          org_admin: "",
        },
        prod: {
          test_org_name: "",
          test_org_id: "",
          org_admin: "",
        },
        startDate: new Date(),
        consultant_id: null,
        nocfReaderSentDate: null,
        nocfReaderDocument: null,
        nocfLicensesSentDate: null,
        nocfLicensesDocument: null,
        portalAccessGrantedDate: null,
      });
      setSelectedProjectOption(null);
      setNocfReaderFile(null);
      setNocfLicensesFile(null);
      setFormSubmitted(false);
    }
  }, [mode, onboardingData, onboardingDetailList]);

  React.useEffect(() => {
    if (mode !== "UPDATE" || !onboardingData || !activeTechnologies.length) {
      return;
    }

    const resolveTechnologyId = (): number | null => {
      if (onboardingData.technology_id && Number(onboardingData.technology_id) > 0) {
        return Number(onboardingData.technology_id);
      }

      const technologyField = onboardingData.technology;
      if (
        technologyField != null &&
        String(technologyField).match(/^\d+$/)
      ) {
        return Number(technologyField);
      }

      if (typeof technologyField === "string") {
        const matchedTechnology = activeTechnologies.find(
          (technology) => technology.name === technologyField
        );
        return matchedTechnology ? Number(matchedTechnology.id) : null;
      }

      return null;
    };

    const resolvedTechnologyId = resolveTechnologyId();
    if (!resolvedTechnologyId) {
      return;
    }

    const technologyExists = activeTechnologies.some(
      (technology) => Number(technology.id) === resolvedTechnologyId
    );
    if (!technologyExists) {
      return;
    }

    setOnboardingDetails((prev) => {
      const technologyChanged =
        Number(prev.technology_id) !== resolvedTechnologyId;
      const integrationsMissing = prev.integration_ids.length === 0;
      const savedIntegrationIds = toSelectedIntegrationIds(
        onboardingData.integration_ids
      );

      if (!technologyChanged && !integrationsMissing) {
        return prev;
      }

      return {
        ...prev,
        technology_id: resolvedTechnologyId,
        integration_ids:
          prev.integration_ids.length > 0
            ? prev.integration_ids
            : savedIntegrationIds,
      };
    });
  }, [mode, onboardingData, activeTechnologies]);

  const handleChange = (
    key: string,
    value: string | number | number[] | Date | [Date, Date] | null | File
  ) => {
    if (key.includes("-")) {
      const [tab, field] = key.split("-");
      if (
        field === "test_org_name" ||
        field === "test_org_id" ||
        field === "org_admin"
      ) {
        setOnboardingDetails((prev) => ({
          ...prev,
          [tab as EnvironmentKey]: {
            ...prev[tab as EnvironmentKey],
            [field]: value,
          },
        }));
      }
    } else {
      setOnboardingDetails((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const handleTechnologyChange = (technologyId: string) => {
    setOnboardingDetails((prev) => ({
      ...prev,
      technology_id: Number(technologyId),
      integration_ids: [],
    }));
  };

  const handleIntegrationToggle = (
    integrationId: number,
    checked: boolean
  ) => {
    setOnboardingDetails((prev) => ({
      ...prev,
      integration_ids: checked
        ? [...prev.integration_ids, integrationId]
        : prev.integration_ids.filter((id) => id !== integrationId),
    }));
  };

  const formatDateForDB = (date: Date | null) => {
    if (date) {
      const formattedDate = date.toLocaleDateString("en-CA");
      return formattedDate;
    }
    return null;
  };

  const hasExistingIntegrationBeenRemoved = (): boolean => {
    if (mode !== "UPDATE" || !onboardingData) {
      return false;
    }
    const originalIds = toSelectedIntegrationIds(onboardingData.integration_ids);
    return originalIds.some(
      (originalId) =>
        !onboardingDetails.integration_ids.some(
          (selectedId) => Number(selectedId) === originalId
        )
    );
  };

  const buildSubmitPayload = (
    confirmIntegrationUpdate?: boolean
  ): OnboardingFormDetails | null => {
    if (!onboardingDetails.technology_id) {
      return null;
    }

    // ADD: only selected integrations with status 1
    // UPDATE: all available integrations for the technology (selected → 1, unselected → 0)
    const integrationPayload =
      mode === "ADD"
        ? onboardingDetails.integration_ids.map((integrationId) => ({
            integration_id: Number(integrationId),
            status: 1 as const,
          }))
        : activeIntegrations.map((integration) => {
            const integrationId = Number(integration.id);
            const isSelected = onboardingDetails.integration_ids.some(
              (id) => Number(id) === integrationId
            );
            return {
              integration_id: integrationId,
              status: (isSelected ? 1 : 0) as 0 | 1,
            };
          });

    const payload: OnboardingFormDetails = {
      ID: mode === "UPDATE" && onboardingData ? onboardingData.ID : 0,
      partner_id: partnerId,
      consultant_id: onboardingDetails.consultant_id || null,
      status: onboardingData?.status || "In Progress",
      technology: onboardingDetails.technology_id,
      integration_ids: integrationPayload,
      project_name: onboardingDetails.projectName,
      cert: onboardingDetails.cert,
      preprod: onboardingDetails.preprod,
      prod: onboardingDetails.prod,
      start_date:
        onboardingData?.start_date || formatDateForDB(new Date()) || "",
      completion_date: onboardingData?.completion_date || "",
      nocf_reader_sent_date: formatDateForDB(
        onboardingDetails.nocfReaderSentDate
      ),
      nocf_reader_document:
        nocfReaderFile || onboardingDetails.nocfReaderDocument || "",
      nocf_licenses_sent_date: formatDateForDB(
        onboardingDetails.nocfLicensesSentDate
      ),
      nocf_licenses_document:
        nocfLicensesFile || onboardingDetails.nocfLicensesDocument || "",
      portal_access_granted_date: formatDateForDB(
        onboardingDetails.portalAccessGrantedDate
      ),
      submitted_by: loggedUserDetail?.ID || 0,
    };

    if (mode === "UPDATE" && confirmIntegrationUpdate !== undefined) {
      payload.confirm_integration_update = confirmIntegrationUpdate;
    }

    return payload;
  };

  const onSubmitClick = () => {
    // Only unselecting an already-saved integration needs the disable confirmation;
    // adding new integrations alone is saved directly.
    const removedExistingIntegration = hasExistingIntegrationBeenRemoved();
    const payload = buildSubmitPayload(
      mode === "UPDATE" ? !removedExistingIntegration : undefined
    );
    if (!payload) {
      return;
    }

    if (mode === "ADD") {
      dispatch(addOnboardingDetailSagaAction(payload));
      setFormSubmitted(true);
      return;
    }

    pendingUpdatePayloadRef.current = payload;
    dispatch(updateOnboardingDetailSagaAction(payload));

    if (removedExistingIntegration) {
      // Wait for API: either confirmation_required (409) or final success/error
      setAwaitingIntegrationConfirmResponse(true);
    } else {
      setFormSubmitted(true);
    }
  };

  const handleIntegrationConfirmContinue = () => {
    const pendingPayload = pendingUpdatePayloadRef.current;
    if (!pendingPayload) {
      return;
    }

    const confirmedPayload: OnboardingFormDetails = {
      ...pendingPayload,
      confirm_integration_update: true,
    };
    pendingUpdatePayloadRef.current = confirmedPayload;
    dispatch(onboardingFormSliceAction.clearUpdateOnboardingConfirmation());
    setShowIntegrationConfirm(false);
    dispatch(updateOnboardingDetailSagaAction(confirmedPayload));
    setFormSubmitted(true);
  };

  const handleIntegrationConfirmCancel = () => {
    dispatch(onboardingFormSliceAction.clearUpdateOnboardingConfirmation());
    setShowIntegrationConfirm(false);
    pendingUpdatePayloadRef.current = null;
    setAwaitingIntegrationConfirmResponse(false);
  };

  const isTechnologySelectionLocked = mode === "UPDATE";

  const technologyOptions = activeTechnologies.map((technology) => ({
    key: technology.id,
    value: technology.name,
  }));

  const consultantOptions = React.useMemo(
    () =>
      userListHID?.map((user) => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.ID,
      })) || [],
    [userListHID]
  );

  const existingProjectOptions = onboardingDetailList.map((project) => ({
    label: project.project_name,
    value: project.project_name,
  }));

  const projectDropdownOptions = [
    {
      label: "New Project",
      value: "New Project",
      options: [
        {
          label: "Enter New Project Name",
          value: "Enter New Project Name",
        },
      ],
    },
    {
      label: "Existing Projects",
      value: "Existing Projects",
      options: [...existingProjectOptions],
    },
  ];

  const onProjectDropdownSelect = (
    data: { label: string; value: string } | null
  ) => {
    setSelectedProjectOption(data);
    if (data?.value === "Enter New Project Name") {
      handleChange("projectName", "");
    } else if (data?.value) {
      handleChange("projectName", data.value);
    } else {
      handleChange("projectName", "");
    }
  };

  const getFileDisplayName = (
    file:
      | OnboardingFormDetails["nocf_reader_document"]
      | OnboardingFormDetails["nocf_licenses_document"]
  ): string => {
    if (file instanceof File) {
      return file.name;
    }
    return file || "";
  };

  const handleFileViewClick = (
    file:
      | OnboardingFormDetails["nocf_reader_document"]
      | OnboardingFormDetails["nocf_licenses_document"]
  ) => {
    // Only dispatch saga action if file is a string (S3 file key), not a File object
    if (typeof file === "string" && file) {
      dispatch(getFileViewUrlSagaAction(file, "view"));
    }
  };

  const isFormValid =
    onboardingDetails.technology_id !== null &&
    onboardingDetails.integration_ids.length > 0 &&
    onboardingDetails.projectName &&
    onboardingDetails.consultant_id !== null;

  const isSubmitting =
    mode === "ADD"
      ? status.addOnboardingDetailLoading
      : status.updateOnboardingDetailLoading;

  const isSuccess =
    mode === "ADD"
      ? status.addOnboardingDetailStatus
      : status.updateOnboardingDetailStatus;

  const statusMessage =
    mode === "ADD"
      ? status.addOnboardingDetailMessage
      : status.updateOnboardingDetailMessage;

  const successMessage =
    statusMessage ||
    (mode === "ADD"
      ? "Onboarding form submitted"
      : "Onboarding form updated");

  const errorMessage = statusMessage || "Something went wrong";

  const environmentTabOptions: { key: EnvironmentKey; label: string }[] = [
    { key: "cert", label: "CERT" },
    { key: "preprod", label: "Preprod" },
    { key: "prod", label: "Prod" },
  ];

  const handleModalClose = () => {
    if (!isSubmitting && !awaitingIntegrationConfirmResponse) {
      dispatch(onboardingFormSliceAction.clearUpdateOnboardingConfirmation());
      setShowIntegrationConfirm(false);
      pendingUpdatePayloadRef.current = null;
      onClose();
      if (formSubmitted && isSuccess) {
        dispatch(getOnboardingDetailListSagaAction(partnerId));
        dispatch(getProjectsListSagaAction(partnerId));
        setFormSubmitted(false);
      }
    }
  };

  const confirmationMessage =
    status.updateOnboardingDetailMessage ||
    "Selected integrations already exist for this project. Do you want to continue?";

  return (
    <>
    <Modal
      optionalHeader="Partner Organizations"
      headerText="Onboard Partner"
      width={920}
      footerConfig={{}}
      onClose={handleModalClose}
      className="onboarding-modal"
    >
      {!formSubmitted ? (
        <ModalHeaderSubtitle text="Configure onboarding details" />
      ) : null}
      {formSubmitted ? (
        <div className="status-message-modal-container">
          {isSubmitting ? (
            <Loader variant={LoaderSizes.large} />
          ) : isSuccess ? (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <div className="message">{successMessage}</div>
              <Button
                variant="primary"
                label="Continue"
                onClick={handleModalClose}
              />
            </>
          ) : (
            <>
              <Iconography
                icon="circleXmark"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Error}
              />
              <div className="message">{errorMessage}</div>
              <Button
                variant="primary"
                label="Continue"
                onClick={handleModalClose}
              />
            </>
          )}
        </div>
      ) : (
        <div className="onboarding-modal-content">
          {awaitingIntegrationConfirmResponse ? (
            <div className="onboarding-modal-content__loading">
              <Loader variant={LoaderSizes.large} />
            </div>
          ) : null}
          <form className="onboarding-form-container onboarding-form-container--modal">
            <FormSection title="Technologies">
              <fieldset
                disabled={isTechnologySelectionLocked}
                className={`technology-radio${
                  isTechnologySelectionLocked ? " technology-radio--locked" : ""
                }`}
              >
                <Radio
                  selectedValue={
                    onboardingDetails.technology_id !== null
                      ? String(onboardingDetails.technology_id)
                      : ""
                  }
                  legend=""
                  radioList={technologyOptions}
                  groupName="technology"
                  isRequired={false}
                  onChange={
                    isTechnologySelectionLocked
                      ? undefined
                      : (_event, key) => handleTechnologyChange(key)
                  }
                />
                {technologySettingsStatus.technologiesLoading &&
                  !technologyOptions.length && (
                    <div className="loader-container">
                      <Loader variant={LoaderSizes.medium} />
                    </div>
                  )}
              </fieldset>
            </FormSection>

            <FormSection title="Integration Type">
              <div className="integration-type-block">
                {selectedTechnology ? (
                  <div className="integration-type-row">
                    <span className="integration-type-row__technology">
                      {selectedTechnology.name}
                    </span>
                    <div className="integration-type-row__options">
                      {activeIntegrations.length > 0 ? (
                        activeIntegrations.map((integration) => {
                          const integrationId = Number(integration.id);
                          const isChecked = onboardingDetails.integration_ids.some(
                            (id) => Number(id) === integrationId
                          );

                          return (
                            <Checkbox
                              key={integration.id}
                              id={`integration-${integration.id}`}
                              name={integration.name}
                              value={integration.id}
                              checked={isChecked}
                              onChange={(_event, result) =>
                                handleIntegrationToggle(
                                  integrationId,
                                  result.checked
                                )
                              }
                            />
                          );
                        })
                      ) : (
                        <span className="integration-type-row__empty">
                          No integrations available
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="integration-type-placeholder">-</span>
                )}
              </div>
            </FormSection>

            <FormSection title="Basic Information">
              <div className="onboarding-form-grid">
                <div className="label-field-container">
                  <FieldLabel label="Project name" required />
                  <Dropdown
                    id="projectName"
                    label=""
                    placeholder="Select"
                    theme="dark"
                    options={projectDropdownOptions}
                    selected={selectedProjectOption}
                    isSearchable={true}
                    menuPortalTarget={dropdownMenuPortalTarget}
                    styles={modalDropdownMenuStyles}
                    onSelect={onProjectDropdownSelect}
                  />
                </div>
                <div className="label-field-container">
                  <FieldLabel label="Technical consultant" required />
                  <Dropdown
                    label=""
                    id="consultant_id"
                    name="consultant_id"
                    options={consultantOptions}
                    selected={consultantOptions?.find(
                      (opt) => opt.value === onboardingDetails.consultant_id
                    )}
                    onSelect={(data) =>
                      handleChange("consultant_id", data?.value || null)
                    }
                    placeholder="Select"
                    theme="dark"
                    menuPortalTarget={dropdownMenuPortalTarget}
                    styles={modalDropdownMenuStyles}
                  />
                </div>
                {selectedProjectOption?.value === "Enter New Project Name" && (
                  <div className="label-field-container">
                    <FieldLabel label="Enter New Project Name" required />
                    <TextField
                      id="projectNameInput"
                      label=""
                      name="projectName"
                      value={onboardingDetails.projectName || ""}
                      theme="dark"
                      onChange={(e) =>
                        handleChange(e.currentTarget.name, e.currentTarget.value)
                      }
                    />
                  </div>
                )}
              </div>
            </FormSection>

            <section className="onboarding-form-section onboarding-form-section--environment">
              <div className="onboarding-environment-card">
                <Tabs
                  theme="frameless"
                  items={environmentTabOptions.map((tab) => ({
                    key: tab.key,
                    label: tab.label,
                    content: (
                      <div className="environment-tab-content">
                        <div className="label-field-container">
                          <FieldLabel label="Test ORG name" />
                          <TextField
                            id={`${tab.key}-test_org_name`}
                            label=""
                            name={`${tab.key}-test_org_name`}
                            value={onboardingDetails[tab.key].test_org_name}
                            theme="dark"
                            onChange={(e) =>
                              handleChange(
                                e.currentTarget.name,
                                e.currentTarget.value
                              )
                            }
                          />
                        </div>
                        <div className="label-field-container">
                          <FieldLabel label="Test ORG ID" />
                          <TextField
                            id={`${tab.key}-test_org_id`}
                            label=""
                            name={`${tab.key}-test_org_id`}
                            value={onboardingDetails[tab.key].test_org_id}
                            theme="dark"
                            onChange={(e) =>
                              handleChange(
                                e.currentTarget.name,
                                e.currentTarget.value
                              )
                            }
                          />
                        </div>
                        <div className="label-field-container">
                          <FieldLabel label="ORG Admin" />
                          <TextField
                            id={`${tab.key}-org_admin`}
                            label=""
                            name={`${tab.key}-org_admin`}
                            value={onboardingDetails[tab.key].org_admin}
                            theme="dark"
                            onChange={(e) =>
                              handleChange(
                                e.currentTarget.name,
                                e.currentTarget.value
                              )
                            }
                          />
                        </div>
                      </div>
                    ),
                  }))}
                  onClick={() => {}}
                />
              </div>
            </section>

            <FormSection title="NCOF INFORMATION">
              <div className="onboarding-contact-grid">
                <div className="label-field-container">
                  <FieldLabel label="NOCF for reader sent date" />
                  <DatePicker
                    id="nocf_reader_sent_date"
                    name="nocf_reader_sent_date"
                    label=""
                    format="dd/MM/yyyy"
                    placeholder="dd/mm/yyyy"
                    value={onboardingDetails.nocfReaderSentDate}
                    onChange={(date) => {
                      handleChange("nocfReaderSentDate", date);
                    }}
                  />
                </div>
                <div className="file-upload-section">
                  <FieldLabel label="Upload NCOF reader document" />
                  <div className="file-upload-hint">
                    Maximum upload file size: 4MB.
                  </div>
                  <div className="file-upload-container">
                    <FileUploader
                      buttonConfig={{ label: "Browse" }}
                      isMultiple={false}
                      onUpload={(e, selectedFiles) => {
                        if (selectedFiles && selectedFiles.length > 0) {
                          const file = selectedFiles[0].fileDetails;
                          if (file instanceof File) {
                            setNocfReaderFile(file);
                            handleChange("nocfReaderDocument", "");
                          }
                        }
                      }}
                      validateConfig={[
                        {
                          maxFileSize: {
                            maxSizeInMb: 4,
                            message: "File exceeds 4MB size limit.",
                          },
                        },
                      ]}
                    />
                  </div>
                  {onboardingDetails.nocfReaderDocument && (
                    <div className="file-display-box">
                      <span
                        className="file-display-name"
                        onClick={() =>
                          handleFileViewClick(onboardingDetails.nocfReaderDocument)
                        }
                      >
                        {getFileDisplayName(onboardingDetails.nocfReaderDocument)}
                      </span>
                      <span
                        className="file-display-close"
                        onClick={() => {
                          setNocfReaderFile(null);
                          handleChange("nocfReaderDocument", "");
                        }}
                      >
                        <Iconography icon="xmark" size={IconographySizes.Small} />
                      </span>
                    </div>
                  )}
                </div>

                <div className="label-field-container">
                  <FieldLabel label="NOCF for licenses sent date" />
                  <DatePicker
                    id="nocf_licenses_sent_date"
                    name="nocf_licenses_sent_date"
                    label=""
                    format="dd/MM/yyyy"
                    placeholder="dd/mm/yyyy"
                    value={onboardingDetails.nocfLicensesSentDate}
                    onChange={(date) => {
                      handleChange("nocfLicensesSentDate", date);
                    }}
                  />
                </div>
                <div className="file-upload-section">
                  <FieldLabel label="Upload NCOF for licenses" />
                  <div className="file-upload-hint">
                    Maximum upload file size: 4MB.
                  </div>
                  <div className="file-upload-container">
                    <FileUploader
                      buttonConfig={{ label: "Browse" }}
                      isMultiple={false}
                      onUpload={(e, selectedFiles) => {
                        if (selectedFiles && selectedFiles.length > 0) {
                          const file = selectedFiles[0].fileDetails;
                          if (file instanceof File) {
                            setNocfLicensesFile(file);
                            handleChange("nocfLicensesDocument", "");
                          }
                        }
                      }}
                      validateConfig={[
                        {
                          maxFileSize: {
                            maxSizeInMb: 4,
                            message: "File exceeds 4MB size limit.",
                          },
                        },
                      ]}
                    />
                  </div>
                  {onboardingDetails.nocfLicensesDocument && (
                    <div className="file-display-box">
                      <span
                        className="file-display-name"
                        onClick={() =>
                          handleFileViewClick(onboardingDetails.nocfLicensesDocument)
                        }
                      >
                        {getFileDisplayName(onboardingDetails.nocfLicensesDocument)}
                      </span>
                      <span
                        className="file-display-close"
                        onClick={() => {
                          setNocfLicensesFile(null);
                          handleChange("nocfLicensesDocument", "");
                        }}
                      >
                        <Iconography icon="xmark" size={IconographySizes.Small} />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </FormSection>

            <FormSection title="Other Information">
              <div className="onboarding-form-grid">
              <div className="label-field-container">
                <FieldLabel label="Portal access granted date" />
                <DatePicker
                  id="portal_access_granted_date"
                  name="portal_access_granted_date"
                  label=""
                  format="dd/MM/yyyy"
                  placeholder="dd/mm/yyyy"
                  value={onboardingDetails.portalAccessGrantedDate}
                  onChange={(date) => {
                    handleChange("portalAccessGrantedDate", date);
                  }}
                />
              </div>
              </div>
            </FormSection>

            <div className="onboarding-modal__footer">
              <Button
                variant="secondary"
                label="Cancel"
                onClick={handleModalClose}
              />
              <Button
                variant="primary"
                label="Submit"
                onClick={onSubmitClick}
                disabled={
                  !isFormValid ||
                  isSubmitting ||
                  awaitingIntegrationConfirmResponse ||
                  showIntegrationConfirm
                }
              />
            </div>
          </form>
        </div>
      )}
    </Modal>
    {showIntegrationConfirm ? (
      <Dialog
        open={showIntegrationConfirm}
        onClose={handleIntegrationConfirmCancel}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: ONBOARDING_MODAL_Z_INDEX + 1000 }}
      >
        <div className="onboarding-integration-confirm-dialog">
          <header className="onboarding-integration-confirm-dialog__header">
            <div className="onboarding-integration-confirm-dialog__heading">
              <span className="onboarding-integration-confirm-dialog__icon">
                <Iconography
                  icon="circleExclamation"
                  size={IconographySizes.Small}
                />
              </span>
              <h3>Update Integration Type?</h3>
            </div>
          </header>
          <DialogContent className="onboarding-integration-confirm-dialog__content">
            <p>
              Please confirm that you want to update the integration type for
              this project.
            </p>
            <div
              className="onboarding-integration-confirm-dialog__warning"
              role="alert"
            >
              <Iconography
                icon="circleExclamation"
                size={IconographySizes.Medium}
              />
              <div>
                <h4>Warning notification</h4>
                <p>{confirmationMessage}</p>
              </div>
            </div>
          </DialogContent>
          <footer className="onboarding-integration-confirm-dialog__footer">
            <Button
              variant="secondary"
              label="CANCEL"
              onClick={handleIntegrationConfirmCancel}
            />
            <Button
              variant="primary"
              label="CONTINUE"
              onClick={handleIntegrationConfirmContinue}
            />
          </footer>
        </div>
      </Dialog>
    ) : null}
    </>
  );
};

export default OnboardingModal;
